import React, { useState, useEffect, useRef } from "react";
import Modal from "components/modal";
import { MdCloudUpload, MdFilePresent, MdDelete } from "react-icons/md";

const MemoireFormModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialMemoire,
  isEditing,
  loading,
}) => {
  const [memoire, setMemoire] = useState({
    memoire_theme: "",
    memoire_description: "",
    memoire_date: new Date().toISOString().split("T")[0],
    memoire_annee: new Date().getFullYear(),
    memoire_etudiant: "",
    memoire_encadrant: "",
    memoire_filiere: "",
    memoire_etablissement: "",
    file: null,
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isOpen && initialMemoire) {
      setMemoire({
        memoire_theme: initialMemoire.memoire_theme || "",
        memoire_description: initialMemoire.memoire_description || "",
        memoire_date:
          initialMemoire.memoire_date || new Date().toISOString().split("T")[0],
        memoire_annee: initialMemoire.memoire_annee || new Date().getFullYear(),
        memoire_etudiant: initialMemoire.memoire_etudiant || "",
        memoire_encadrant: initialMemoire.memoire_encadrant || "",
        memoire_filiere: initialMemoire.memoire_filiere || "",
        memoire_etablissement: initialMemoire.memoire_etablissement || "",
        file: null,
      });
      setFileName(initialMemoire.memoire_fichier || "");
      setValidationErrors({});
    }
  }, [isOpen, initialMemoire]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "memoire_annee") {
      const year = parseInt(value, 10);
      if (!isNaN(year)) {
        setMemoire({ ...memoire, [name]: year });
      }
    } else {
      setMemoire({ ...memoire, [name]: value });
    }

    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: null,
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Vérifier que c'est un PDF
      if (file.type !== "application/pdf") {
        setValidationErrors({
          ...validationErrors,
          file: "Seuls les fichiers PDF sont acceptés",
        });
        return;
      }

      // Vérifier la taille du fichier (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setValidationErrors({
          ...validationErrors,
          file: "Le fichier est trop volumineux (max 10MB)",
        });
        return;
      }

      setMemoire({ ...memoire, file });
      setFileName(file.name);
      setValidationErrors({
        ...validationErrors,
        file: null,
      });
    }
  };

  const removeFile = () => {
    setMemoire({ ...memoire, file: null });
    setFileName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!memoire.memoire_theme.trim()) {
      errors.memoire_theme = "Le thème du mémoire est requis";
    }

    if (!memoire.memoire_etudiant.trim()) {
      errors.memoire_etudiant = "Le nom de l'étudiant est requis";
    }

    if (!memoire.memoire_encadrant.trim()) {
      errors.memoire_encadrant = "Le nom de l'encadrant est requis";
    }

    if (!memoire.memoire_date) {
      errors.memoire_date = "La date est requise";
    }

    if (!memoire.memoire_annee || isNaN(memoire.memoire_annee)) {
      errors.memoire_annee = "L'année est requise";
    } else if (memoire.memoire_annee < 1900 || memoire.memoire_annee > 2100) {
      errors.memoire_annee = "L'année doit être entre 1900 et 2100";
    }

    if (!memoire.memoire_filiere.trim()) {
      errors.memoire_filiere = "La filière est requise";
    }

    if (!memoire.memoire_etablissement.trim()) {
      errors.memoire_etablissement = "L'établissement est requis";
    }

    // Vérifier si un fichier est requis lors de la création
    if (!isEditing && !memoire.file) {
      errors.file = "Le fichier PDF du mémoire est requis";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    const result = await onSubmit(memoire);

    if (!result.success) {
      setValidationErrors(result.errors || {});
    }

    setSubmitting(false);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} width="2xl">
      <div className="text-center sm:text-left">
        <div className="mb-6">
          <h3 className="text-xl font-semibold text-gray-800">
            {isEditing ? "Modifier le mémoire" : "Ajouter un nouveau mémoire"}
          </h3>
          <div className="mt-2 h-1 w-12 rounded-full bg-indigo-600"></div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="memoire_theme"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Thème du mémoire *
              </label>
              <input
                type="text"
                name="memoire_theme"
                id="memoire_theme"
                value={memoire.memoire_theme}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder="Ex: Intelligence Artificielle"
              />
              {validationErrors.memoire_theme && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.memoire_theme}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="memoire_annee"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Année académique *
              </label>
              <input
                type="number"
                name="memoire_annee"
                id="memoire_annee"
                min="1900"
                max="2100"
                value={memoire.memoire_annee}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder="Ex: 2024"
              />
              {validationErrors.memoire_annee && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.memoire_annee}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="memoire_etudiant"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Nom de l'étudiant *
              </label>
              <input
                type="text"
                name="memoire_etudiant"
                id="memoire_etudiant"
                value={memoire.memoire_etudiant}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder="Ex: John Doe"
              />
              {validationErrors.memoire_etudiant && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.memoire_etudiant}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="memoire_encadrant"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Nom de l'encadrant *
              </label>
              <input
                type="text"
                name="memoire_encadrant"
                id="memoire_encadrant"
                value={memoire.memoire_encadrant}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder="Ex: Dr. Dupont"
              />
              {validationErrors.memoire_encadrant && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.memoire_encadrant}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="memoire_filiere"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Filière *
              </label>
              <input
                type="text"
                name="memoire_filiere"
                id="memoire_filiere"
                value={memoire.memoire_filiere}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder="Ex: Informatique"
              />
              {validationErrors.memoire_filiere && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.memoire_filiere}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="memoire_etablissement"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Établissement *
              </label>
              <input
                type="text"
                name="memoire_etablissement"
                id="memoire_etablissement"
                value={memoire.memoire_etablissement}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
                placeholder="Ex: Université de Madagascar"
              />
              {validationErrors.memoire_etablissement && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.memoire_etablissement}
                </p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label
                htmlFor="memoire_date"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Date de soutenance *
              </label>
              <input
                type="date"
                name="memoire_date"
                id="memoire_date"
                value={memoire.memoire_date}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              />
              {validationErrors.memoire_date && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.memoire_date}
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="file"
                className="mb-1 block text-sm font-medium text-gray-700"
              >
                Fichier PDF {!isEditing && "*"}
              </label>
              <div className="mt-1 flex justify-center rounded-md border-2 border-dashed border-gray-300 px-6 pb-6 pt-5">
                <div className="space-y-1 text-center">
                  {fileName ? (
                    <div className="flex flex-col items-center">
                      <MdFilePresent className="mx-auto h-12 w-12 text-indigo-500" />
                      <p className="text-sm text-gray-600">{fileName}</p>
                      <button
                        type="button"
                        onClick={removeFile}
                        className="border-transparent mt-2 inline-flex items-center rounded border bg-red-100 px-2.5 py-1.5 text-xs font-medium text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        <MdDelete className="mr-1 h-4 w-4" /> Supprimer
                      </button>
                    </div>
                  ) : (
                    <>
                      <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                      <div className="flex text-sm text-gray-600">
                        <label
                          htmlFor="file-upload"
                          className="relative cursor-pointer rounded-md font-medium text-indigo-600 focus-within:outline-none focus-within:ring-2 focus-within:ring-indigo-500 focus-within:ring-offset-2 hover:text-indigo-500"
                        >
                          <span>Sélectionner un fichier</span>
                          <input
                            id="file-upload"
                            name="file-upload"
                            type="file"
                            ref={fileInputRef}
                            accept=".pdf"
                            onChange={handleFileChange}
                            className="sr-only"
                          />
                        </label>
                        <p className="pl-1">ou glisser-déposer</p>
                      </div>
                      <p className="text-xs text-gray-500">PDF jusqu'à 10MB</p>
                    </>
                  )}
                </div>
              </div>
              {validationErrors.file && (
                <p className="mt-1.5 text-sm text-red-600">
                  {validationErrors.file}
                </p>
              )}
            </div>
          </div>

          <div>
            <label
              htmlFor="memoire_description"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Description
            </label>
            <textarea
              name="memoire_description"
              id="memoire_description"
              rows="4"
              value={memoire.memoire_description}
              onChange={handleInputChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
              placeholder="Description du mémoire..."
            ></textarea>
          </div>

          {validationErrors.general && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3">
              <p className="text-sm text-red-600">{validationErrors.general}</p>
            </div>
          )}

          <div className="mt-8 flex justify-end gap-3">
            <button
              type="button"
              className="rounded-md border border-gray-300 bg-white px-4 py-2 text-gray-600 transition-colors hover:bg-gray-50"
              onClick={onClose}
              disabled={submitting || loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="rounded-md bg-indigo-600 px-4 py-2 text-white shadow-sm transition-colors hover:bg-indigo-700"
              disabled={submitting || loading}
            >
              {submitting || loading ? (
                <span className="flex items-center">
                  <svg
                    className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Chargement...
                </span>
              ) : isEditing ? (
                "Mettre à jour"
              ) : (
                "Enregistrer"
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default MemoireFormModal;
